import { useEffect, useMemo, useRef, useState } from "react";
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Tabs,
  Typography,
} from "antd";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { convertIntakeToProject, listUsers, updateIntake } from "../lib/api";
import type { ProjectTemplate } from "../lib/types";
import agencyIntakeSchema from "../data/agency_intake_schema.json";
import { useAuth } from "../lib/auth";
import { useApexLayout } from "./apex/ApexProvider";
import AnswersTab from "./intakeDetails/AnswersTab";
import MetadataTab from "./intakeDetails/MetadataTab";
import WorkflowTab from "./intakeDetails/WorkflowTab";
import type { IntakeAnswer, IntakeItem, VendorRef as Vendor } from "./intakeDetails/types";
import { INTAKE_SOURCES, type IntakeSource } from "./intakeDetails/types";

interface CommentItem {
  id: string;
  intakeId: string;
  message: string;
  createdAt?: string;
  author?: string;
}

interface IntakeDetailsModalProps {
  intake: IntakeItem;
  vendors: Vendor[];
  onClose: () => void;
}

type TabType = "summary" | "details" | "metadata" | "workflow" | "comments" | "notes";

export default function IntakeDetailsModal({ intake, vendors, onClose }: IntakeDetailsModalProps) {
  const { message, modal } = App.useApp();
  const { user, isAdmin, canWrite } = useAuth();
  // Clip the modal + its mask so they don't paint over the Apex side panel.
  const { drawerOpen: apexOpen, apexWidth, pushMode } = useApexLayout();
  const apexOffset = apexOpen && pushMode ? apexWidth : 0;
  const [activeTab, setActiveTab] = useState<TabType>("summary");
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [newComment, setNewComment] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [notesDraft, setNotesDraft] = useState(intake.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const notesSaveTimeoutRef = useRef<number | null>(null);
  const [form] = Form.useForm();

  // OAIP-44 — convert-with-template state.
  const NONE_TEMPLATE = "__none__";
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [templateRoles, setTemplateRoles] = useState<string[]>([]);
  const [roleMap, setRoleMap] = useState<Record<string, string>>({});
  const [convertUsers, setConvertUsers] = useState<Array<{ uid: string; displayName?: string; email?: string }>>([]);

  const activeTemplateId =
    selectedTemplateId && selectedTemplateId !== NONE_TEMPLATE ? selectedTemplateId : null;

  // Live template library (for the convert picker).
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "projectTemplates"), (snap) => {
      setTemplates(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ProjectTemplate, "id">) })));
    });
    return () => unsub();
  }, []);

  // Pre-select the pinned default when the convert dialog opens; reset on close.
  useEffect(() => {
    if (!convertOpen) {
      setSelectedTemplateId("");
      setRoleMap({});
      return;
    }
    const def = templates.find((t) => t.isDefault);
    setSelectedTemplateId((prev) => (prev ? prev : def ? def.id : NONE_TEMPLATE));
  }, [convertOpen, templates]);

  // Load the allowlist users once the dialog opens (for the role-mapping picker).
  useEffect(() => {
    if (!convertOpen) return;
    listUsers()
      .then((u) => setConvertUsers(Array.isArray(u) ? u : []))
      .catch(() => setConvertUsers([]));
  }, [convertOpen]);

  // Derive the distinct role placeholders for the selected template.
  useEffect(() => {
    if (!convertOpen || !activeTemplateId) {
      setTemplateRoles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const snap = await getDocs(
        query(collection(db, "projectTemplateSubtasks"), where("templateId", "==", activeTemplateId)),
      );
      const roles = new Set<string>();
      snap.forEach((d) => {
        const role = (d.data() as { assigneeRole?: string }).assigneeRole;
        if (typeof role === "string" && role.trim()) roles.add(role.trim());
      });
      if (!cancelled) {
        setTemplateRoles(Array.from(roles).sort());
        setRoleMap({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [convertOpen, activeTemplateId]);

  const normalizedStatus = useMemo(() => {
    if (intake.status === "draft-ready" || intake.status === "in-progress") {
      return "Submitted";
    }
    return intake.status || "Submitted";
  }, [intake.status]);

  useEffect(() => {
    setNotesDraft(intake.notes || "");
  }, [intake.notes]);

  const intakeSchema = useMemo(() => {
    if (Array.isArray(intake.intakeSchema) && intake.intakeSchema.length > 0) {
      return intake.intakeSchema;
    }
    if (intake.type === "agency") {
      return agencyIntakeSchema as Array<{ id?: string; questionId?: string; stepTitle?: string; question?: string }>;
    }
    return [];
  }, [intake.intakeSchema, intake.type]);

  const normalizeQuestion = (text?: string) =>
    (text || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const getAnswer = (questionId: string) => {
    const schemaItem = intakeSchema.find((item) => (item.id || item.questionId) === questionId);
    const questionText = schemaItem?.question || "";
    const normalizedSchemaQuestion = normalizeQuestion(questionText);

    const summaryAnswers = intake.summaryAnswers || [];
    const exactMatch = summaryAnswers.find(
      (item) => normalizeQuestion(item.question) === normalizedSchemaQuestion
    );
    if (exactMatch?.answer) return exactMatch.answer;

    const containsMatch = summaryAnswers.find((item) => {
      const normalizedItemQuestion = normalizeQuestion(item.question);
      return (
        normalizedItemQuestion.includes(normalizedSchemaQuestion) ||
        normalizedSchemaQuestion.includes(normalizedItemQuestion)
      );
    });

    return containsMatch?.answer || "";
  };

  const problemAnswer = getAnswer("problem") || intake.problemBeingSolved || "";
  const impactAnswer = getAnswer("current_measurement") || intake.currentProblemImpact || "";

  const displayAnswers = useMemo(() => {
    if (intake.summaryAnswers && intake.summaryAnswers.length > 0) {
      return intake.summaryAnswers;
    }
    if (intakeSchema.length > 0) {
      return intakeSchema.map((item) => {
        const questionId = item.id || item.questionId || "";
        return {
          questionId,
          stepTitle: item.stepTitle,
          question: item.question,
          answer: questionId
            ? intake.summaryAnswers?.find((answer) => answer.questionId === questionId)?.answer || ""
            : "",
        };
      });
    }
    return [] as IntakeAnswer[];
  }, [intake.summaryAnswers, intakeSchema]);


  const getStatusColor = (status: string) => {
    switch (status) {
      case "Submitted":
        return "default";
      case "Review Complete":
        return "blue";
      case "Initial Intake Call Complete":
        return "geekblue";
      case "Assessment Complete":
        return "purple";
      case "Advisory Board Review Complete":
        return "cyan";
      case "Leadership Review Complete":
        return "green";
      case "On Hold":
        return "gold";
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  useEffect(() => {
    const commentsQuery = query(
      collection(db, "intakeComments"),
      where("intakeId", "==", intake.id)
    );
    const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
      const items = snapshot.docs.map((docItem) => ({
        ...(docItem.data() as CommentItem),
        id: docItem.id,
      }));
      items.sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
      setComments(items);
    });
    return () => unsubscribe();
  }, [intake.id]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    await addDoc(collection(db, "intakeComments"), {
      intakeId: intake.id,
      message: newComment.trim(),
      createdAt: new Date().toISOString(),
      author: user?.displayName || user?.email || "Unknown",
    });
    setNewComment("");
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    const author = user?.displayName || user?.email || "Unknown";
    // OAIP-76: route through audited backend so this status change lands
    // in `auditLog` along with everything else.
    await updateIntake(intake.id, {
      status: "Rejected",
      rejectionReason: rejectReason.trim(),
      archived: true,
    });
    await addDoc(collection(db, "intakeComments"), {
      intakeId: intake.id,
      message: `[Auto-generated] Rejected: ${rejectReason.trim()}`,
      createdAt: new Date().toISOString(),
      author,
    });
    setRejectReason("");
    setRejectOpen(false);
  };

  const handleHold = async () => {
    if (!holdReason.trim()) return;
    const author = user?.displayName || user?.email || "Unknown";
    await updateIntake(intake.id, {
      status: "On Hold",
      onHoldReason: holdReason.trim(),
    });
    await addDoc(collection(db, "intakeComments"), {
      intakeId: intake.id,
      message: `[Auto-generated] Placed on hold: ${holdReason.trim()}`,
      createdAt: new Date().toISOString(),
      author,
    });
    setHoldReason("");
    setHoldOpen(false);
  };

  const handleDeleteIntake = async () => {
    modal.confirm({
      title: "Delete intake",
      content: "Are you sure you want to delete this intake? This cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const batch = writeBatch(db);

        const commentsSnapshot = await getDocs(
          query(collection(db, "intakeComments"), where("intakeId", "==", intake.id))
        );
        commentsSnapshot.forEach((docSnap) => batch.delete(docSnap.ref));

        batch.delete(doc(db, "publicIntakes", intake.id));
        await batch.commit();

        message.success("Intake deleted successfully");
        onClose();
      },
    });
  };

  const handleConvertToProject = async () => {
    try {
      const values = await form.validateFields();
      setConverting(true);

      const projectDescriptionParts = [problemAnswer, impactAnswer].filter(Boolean);
      const projectDescription = projectDescriptionParts.join("\n\n");

      const intakeFields = {
        problemBeingSolved: problemAnswer,
        currentProblemImpact: impactAnswer,
        successIndicators: getAnswer("success_metrics"),
        successMetrics: getAnswer("success_metrics"),
        impactReach: getAnswer("impact_reach"),
        dataAvailability: getAnswer("data_availability"),
        dataSources: getAnswer("data_availability"),
        risks: getAnswer("risks"),
        executiveSupport: getAnswer("executive_support"),
      };

      // OAIP-44: the synthesised project fields. The backend
      // `apexConvertIntakeToProjectHandler` validates these against the project
      // field allowlist, creates the project, stamps the intake, writes the
      // auto-comment, and (when a template is chosen) applies it — all audited
      // in one call. `createdAt`/`createdBy` are stamped server-side.
      const projectFields = {
        name: values.name,
        status: values.status || "Submitted",
        source: intake.source || "Intake Form",
        startDate: values.startDate ? values.startDate.format("YYYY-MM-DD") : "",
        endDate: values.endDate ? values.endDate.format("YYYY-MM-DD") : "",
        vendorName: values.vendorName || "",
        vendorPartners: values.vendorName ? [values.vendorName] : [],
        projectDescription,
        ...intakeFields,
      };

      const result = await convertIntakeToProject({
        intakeId: intake.id,
        projectFields,
        templateId: activeTemplateId,
        roleMap: activeTemplateId ? roleMap : undefined,
      });

      const applied = activeTemplateId
        ? ` Applied template: ${result.counts.stages} stages, ${result.counts.tasks} tasks, ` +
          `${result.counts.subtasks} subtasks, ${result.counts.metrics} metrics.`
        : "";
      message.success(`Intake converted to project.${applied}`);
      setConvertOpen(false);
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
        console.error("Failed to convert intake:", error.message);
      }
    } finally {
      setConverting(false);
    }
  };

  const saveNotesNow = async (nextNotes?: string) => {
    const trimmedDraft = (nextNotes ?? notesDraft).trim();
    const trimmedSaved = (intake.notes || "").trim();

    if (trimmedDraft === trimmedSaved) {
      return;
    }

    try {
      setSavingNotes(true);
      await updateIntake(intake.id, { notes: trimmedDraft });
    } catch (error) {
      message.error("Failed to auto-save notes.");
      console.error("Failed to auto-save notes:", error);
    } finally {
      setSavingNotes(false);
    }
  };

  useEffect(() => {
    const trimmedDraft = notesDraft.trim();
    const trimmedSaved = (intake.notes || "").trim();

    if (trimmedDraft === trimmedSaved) {
      return;
    }

    if (notesSaveTimeoutRef.current) {
      window.clearTimeout(notesSaveTimeoutRef.current);
    }

    notesSaveTimeoutRef.current = window.setTimeout(() => {
      saveNotesNow();
    }, 300);

    return () => {
      if (notesSaveTimeoutRef.current) {
        window.clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
    };
  }, [notesDraft, intake.id, intake.notes]);

  const handleClose = async () => {
    if (notesSaveTimeoutRef.current) {
      window.clearTimeout(notesSaveTimeoutRef.current);
      notesSaveTimeoutRef.current = null;
    }
    await saveNotesNow();
    onClose();
  };

  const handleDeleteComment = async (comment: CommentItem) => {
    await deleteDoc(doc(db, "intakeComments", comment.id));
  };

  return (
    <>
      <Modal
        title={intake.intakeName || "Intake Details"}
        open
        onCancel={handleClose}
        footer={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            {isAdmin ? (
              <Button danger onClick={handleDeleteIntake}>
                Delete Intake
              </Button>
            ) : (
              <span />
            )}
            <Button onClick={handleClose}>Close</Button>
          </Space>
        }
        width={1000}
        destroyOnClose
        styles={{ mask: { right: apexOffset }, wrapper: { right: apexOffset } }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Typography.Text type="secondary">{intake.type || "agency"} intake</Typography.Text>
            <Typography.Title level={3} style={{ marginBottom: 0 }}>
              {intake.intakeName || "Intake"}
            </Typography.Title>
            {intake.intakeDescription && (
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {intake.intakeDescription}
              </Typography.Paragraph>
            )}
            <Space wrap size="small" style={{ marginTop: 8 }}>
              <Tag color={getStatusColor(normalizedStatus)}>{normalizedStatus}</Tag>
              {intake.archived && <Tag color="default">Archived</Tag>}
              {intake.convertedProjectId && <Tag color="processing">Converted</Tag>}
            </Space>
          </div>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as TabType)}
                items={[
                  {
                    key: "summary",
                    label: "Summary",
                    children: (
                      <Space direction="vertical" size="large" style={{ width: "100%" }}>
                        <Card>
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <Typography.Text type="secondary">Status</Typography.Text>
                              <div style={{ marginTop: 4 }}>
                                <Tag color={getStatusColor(normalizedStatus)}>{normalizedStatus}</Tag>
                              </div>
                            </Col>
                            <Col span={12}>
                              <Typography.Text type="secondary">Submitted</Typography.Text>
                              <Typography.Title level={5} style={{ marginTop: 4 }}>
                                {intake.submittedAt || intake.createdAt
                                  ? new Date(intake.submittedAt || intake.createdAt || "").toLocaleString()
                                  : "—"}
                              </Typography.Title>
                            </Col>
                          </Row>
                          <Divider />
                          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                            <div>
                              <Typography.Text type="secondary">Submitter</Typography.Text>
                              <Typography.Paragraph style={{ marginBottom: 0 }}>
                                {intake.submitterName || "—"}
                              </Typography.Paragraph>
                              <Typography.Text type="secondary">Agency</Typography.Text>
                              <Typography.Paragraph style={{ marginBottom: 0 }}>
                                {intake.submitterAgency || "—"}
                              </Typography.Paragraph>
                              <Typography.Text type="secondary">Email</Typography.Text>
                              <Typography.Paragraph style={{ marginBottom: 0 }}>
                                {intake.submitterEmail || "—"}
                              </Typography.Paragraph>
                              <Typography.Text type="secondary">Source</Typography.Text>
                              <div style={{ marginTop: 4 }}>
                                <Select<IntakeSource>
                                  value={intake.source || "Intake Form"}
                                  size="small"
                                  onChange={async (value) => {
                                    try {
                                      await updateIntake(intake.id, { source: value });
                                      message.success("Source updated");
                                    } catch (error) {
                                      if (error instanceof Error) message.error(error.message);
                                    }
                                  }}
                                  options={INTAKE_SOURCES.map((s) => ({ label: s, value: s }))}
                                  style={{ minWidth: 160 }}
                                  aria-label="Intake source"
                                  disabled={!canWrite}
                                />
                              </div>
                            </div>
                            <div>
                              <Typography.Text type="secondary">Problem being solved</Typography.Text>
                              <Typography.Paragraph>{problemAnswer || "—"}</Typography.Paragraph>
                            </div>
                            <div>
                              <Typography.Text type="secondary">Current problem impact</Typography.Text>
                              <Typography.Paragraph>{impactAnswer || "—"}</Typography.Paragraph>
                            </div>
                          </Space>
                        </Card>
                        {canWrite && (
                          <Space wrap>
                            <Button
                              type="primary"
                              onClick={() => {
                                form.setFieldsValue({
                                  name: problemAnswer ? `AI Intake - ${problemAnswer.slice(0, 40)}` : "",
                                  status: "Submitted",
                                });
                                setConvertOpen(true);
                              }}
                              disabled={Boolean(intake.convertedProjectId)}
                            >
                              Convert to Project
                            </Button>
                            <Button onClick={() => setHoldOpen(true)}>Put on Hold</Button>
                            <Button danger onClick={() => setRejectOpen(true)}>
                              Reject
                            </Button>
                          </Space>
                        )}
                        {(intake.rejectionReason || intake.onHoldReason) && (
                          <Card>
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                              {intake.rejectionReason && (
                                <div>
                                  <Typography.Text type="secondary">Rejection Reason</Typography.Text>
                                  <Typography.Paragraph>{intake.rejectionReason}</Typography.Paragraph>
                                </div>
                              )}
                              {intake.onHoldReason && (
                                <div>
                                  <Typography.Text type="secondary">On Hold Reason</Typography.Text>
                                  <Typography.Paragraph>{intake.onHoldReason}</Typography.Paragraph>
                                </div>
                              )}
                            </Space>
                          </Card>
                        )}
                      </Space>
                    ),
                  },
                  {
                    key: "details",
                    label: "Intake Answers",
                    children: (
                      <AnswersTab
                        answers={displayAnswers}
                        canEdit={canWrite}
                        // OAIP-76: route through audited backend so the
                        // diff lands in `auditLog`. Persist only the
                        // {questionId, answer} pairs the schema knows
                        // about; the backend re-validates anyway.
                        onAnswerEdit={async (next) => {
                          await updateIntake(intake.id, {
                            summaryAnswers: next
                              .filter((a) => a.questionId)
                              .map((a) => ({
                                questionId: a.questionId,
                                answer: a.answer || "",
                              })),
                          });
                          message.success("Answer updated");
                        }}
                      />
                    ),
                  },
                  {
                    key: "metadata",
                    label: "Metadata",
                    children: (
                      <MetadataTab
                        intake={intake}
                        canEdit={canWrite}
                        onFieldEdit={async (field, value) => {
                          await updateIntake(intake.id, { [field]: value });
                          message.success(`${String(field)} updated`);
                        }}
                      />
                    ),
                  },
                  {
                    key: "workflow",
                    label: "Workflow",
                    children: <WorkflowTab intakeId={intake.id} />,
                  },
                  {
                    key: "notes",
                    label: "Notes",
                    children: (
                      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                        <Card>
                          <Space direction="vertical" size="small" style={{ width: "100%" }}>
                            <Input.TextArea
                              value={notesDraft}
                              onChange={(event) => setNotesDraft(event.target.value)}
                              placeholder="Add internal notes for this intake"
                              autoSize={{ minRows: 6 }}
                            />
                            <Typography.Text type="secondary">
                              {savingNotes ? "Saving…" : "Auto-saved"}
                            </Typography.Text>
                          </Space>
                        </Card>
                      </Space>
                    ),
                  },
                  {
                    key: "comments",
                    label: `Comments (${comments.length})`,
                    children: (
                      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                        <Card>
                          <Space direction="vertical" size="small" style={{ width: "100%" }}>
                            <Input.TextArea
                              value={newComment}
                              onChange={(event) => setNewComment(event.target.value)}
                              placeholder="Add a comment"
                              autoSize={{ minRows: 3 }}
                            />
                            <Button type="primary" onClick={handleAddComment} disabled={!newComment.trim()}>
                              Add Comment
                            </Button>
                          </Space>
                        </Card>
                        <Card title="Comment History">
                          {comments.length === 0 ? (
                            <Typography.Text type="secondary">No comments yet.</Typography.Text>
                          ) : (
                            <List
                              dataSource={comments}
                              renderItem={(comment) => (
                                <List.Item>
                                  <List.Item.Meta
                                    title={comment.author || "Unknown"}
                                    description={comment.message}
                                  />
                                  <Space direction="vertical" size={0} align="end">
                                    <Typography.Text type="secondary">
                                      {comment.createdAt
                                        ? new Date(comment.createdAt).toLocaleString()
                                        : ""}
                                    </Typography.Text>
                                    {comment.author &&
                                      comment.author === (user?.displayName || user?.email || "") &&
                                      !comment.message.startsWith("[Auto-generated]") && (
                                        <Button
                                          type="link"
                                          danger
                                          onClick={() => handleDeleteComment(comment)}
                                        >
                                          Delete
                                        </Button>
                                      )}
                                  </Space>
                                </List.Item>
                              )}
                            />
                          )}
                        </Card>
                      </Space>
                    ),
                  },
                ]}
              />
            </Col>
          </Row>
        </Space>
      </Modal>

      <Modal
        title="Reject Intake"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ danger: true, disabled: !rejectReason.trim() }}
        styles={{ mask: { right: apexOffset }, wrapper: { right: apexOffset } }}
      >
        <Input.TextArea
          value={rejectReason}
          onChange={(event) => setRejectReason(event.target.value)}
          placeholder="Provide a rejection reason"
          autoSize={{ minRows: 3 }}
        />
      </Modal>

      <Modal
        title="Put Intake on Hold"
        open={holdOpen}
        onCancel={() => setHoldOpen(false)}
        onOk={handleHold}
        okText="Put on Hold"
        okButtonProps={{ disabled: !holdReason.trim() }}
        styles={{ mask: { right: apexOffset }, wrapper: { right: apexOffset } }}
      >
        <Input.TextArea
          value={holdReason}
          onChange={(event) => setHoldReason(event.target.value)}
          placeholder="Provide an on-hold reason"
          autoSize={{ minRows: 3 }}
        />
      </Modal>

      <Modal
        title="Convert Intake to Project"
        open={convertOpen}
        onCancel={() => setConvertOpen(false)}
        onOk={handleConvertToProject}
        okText={converting ? "Converting..." : "Convert"}
        okButtonProps={{ loading: converting }}
        styles={{ mask: { right: apexOffset }, wrapper: { right: apexOffset } }}
      >
        <Form layout="vertical" form={form}>
          <Form.Item label="Project name" name="name" rules={[{ required: true, message: "Enter a project name" }]}
          >
            <Input placeholder="Project name" />
          </Form.Item>
          <Form.Item label="Status" name="status">
            <Select
              options={[
                "Submitted",
                "In Progress",
                "Completed",
                "On Hold",
              ].map((status) => ({ label: status, value: status }))}
              showSearch={false}
            />
          </Form.Item>
          <Form.Item label="Start date" name="startDate">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="End date" name="endDate">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Vendor partner" name="vendorName">
            <Select
              allowClear
              options={vendors.map((vendor) => ({
                label: vendor.profile?.split("\n")[0] || "Vendor",
                value: vendor.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Start from template"
            tooltip="Pre-fill the project plan (stages, tasks, subtasks, metrics) from a reusable template. Dates resolve from the start date above."
          >
            <Select
              value={selectedTemplateId || undefined}
              onChange={setSelectedTemplateId}
              options={[
                { label: "(none — blank project)", value: NONE_TEMPLATE },
                ...templates.map((t) => ({
                  label: `${t.name}${t.isDefault ? " (default)" : ""}`,
                  value: t.id,
                })),
              ]}
            />
          </Form.Item>

          {activeTemplateId && templateRoles.length > 0 && (
            <>
              <Divider style={{ margin: "8px 0" }} />
              <Typography.Text strong>Map template roles to users</Typography.Text>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                This template assigns subtasks by role. Pick a user for each (optional — leave blank
                to create the subtask unassigned).
              </Typography.Paragraph>
              {templateRoles.map((role) => (
                <Form.Item key={role} label={role} style={{ marginBottom: 8 }}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Leave unassigned"
                    value={roleMap[role]}
                    onChange={(val) =>
                      setRoleMap((prev) => {
                        const next = { ...prev };
                        if (val) next[role] = val;
                        else delete next[role];
                        return next;
                      })
                    }
                    options={convertUsers.map((u) => ({
                      value: u.uid,
                      label: u.displayName || u.email || u.uid,
                    }))}
                  />
                </Form.Item>
              ))}
            </>
          )}
        </Form>
      </Modal>
    </>
  );
}
